/**
 * CR-5534: Quality Approval Register.
 *
 * Suppliers × Accreditations matrix with RAG status driven by each
 * binding's expiry date and a per-tenant warning window
 * (PlatformSettings.accreditation_expiry_warning_days). Customer admins
 * can add / edit / delete bindings; supplier users see only their own
 * row in read-only mode.
 *
 * Replaces the legacy SupplierPortal Approval Register
 * (LM_T_Company_Approval_Bind + LM_T_Catalogue CatalogueType=2). The
 * existing /intelligence/qualifications page (onboarding questionnaire
 * matrix) is left untouched.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { qualityAPI, platformSettingsAPI } from '../lib/api';
import type {
  ApprovalRegisterCell,
  ApprovalRegisterMatrix,
  QualityTabsVisibility,
} from '../lib/api';
import { useIsCustomer } from '../hooks/usePortalMode';
import { useFormatters } from '../contexts/PreferencesContext';
import { useView } from '../contexts/ViewContext';

import { ApprovalRegisterEditDialog } from './ApprovalRegisterEditDialog';
import {
  DetailFileLink,
  DetailRow,
  LegendItem,
  MatrixCell,
  STATUS_STYLES,
} from './ApprovalRegisterCell';

interface CellMeta {
  cell: ApprovalRegisterCell;
  accreditationName: string;
  supplierName: string;
}

export default function ApprovalRegister() {
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const { formatDate } = useFormatters();
  // CR-5534: matrix must follow the top-bar hierarchy picker so a
  // Helix admin viewing "ACME — AEROSPACE" only sees ACME's suppliers.
  const { getViewQueryParams, viewKey, isLoading: viewLoading } = useView();

  const [matrix, setMatrix] = useState<ApprovalRegisterMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true,
    concessions: true,
    approval_register: true,
  });
  // Same rule as Quality.tsx / Concessions.tsx: the tab needs the admin's
  // tab-visibility choice AND the tenant's concession feature switch.
  // Without the second check this strip offered "Production Permit /
  // Concession" on tenants where /concessions just bounces back to /quality.
  const [concessionsEnabled, setConcessionsEnabled] = useState(false);
  const showConcessionsTab = tabVisibility.concessions && concessionsEnabled;

  // Detail modal state.
  const [detailMeta, setDetailMeta] = useState<CellMeta | null>(null);

  // Edit dialog state.
  const [editOpen, setEditOpen] = useState(false);
  const [editBindingId, setEditBindingId] = useState<number | null>(null);
  const [prefillSupplierId, setPrefillSupplierId] = useState<number | null>(null);
  const [prefillAccreditationId, setPrefillAccreditationId] = useState<number | null>(null);

  // Confirm-delete dialog state.
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMatrix = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await qualityAPI.getApprovalRegister(getViewQueryParams());
      setMatrix(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load Approval Register';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [getViewQueryParams]);

  useEffect(() => {
    if (viewLoading) return;
    fetchMatrix();
    // viewKey changes whenever the top-bar picker selects a new site /
    // company / group, refetching the matrix with the new scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMatrix, viewLoading, viewKey]);

  // Tab visibility — keep parity with Quality.tsx so the tab strip
  // honours the platform-settings toggles.
  useEffect(() => {
    platformSettingsAPI
      .getDisplaySettings()
      .then((res) => {
        if (res.data?.quality_tabs) {
          setTabVisibility((prev) => ({ ...prev, ...res.data.quality_tabs }));
        }
        if (res.data) {
          setConcessionsEnabled(!!res.data.concessions_enabled);
        }
      })
      .catch(() => undefined);
  }, []);

  // Cell lookup: key by `supplier_id:accreditation_id` for O(1) access.
  const cellIndex = useMemo(() => {
    const map = new Map<string, ApprovalRegisterCell>();
    matrix?.cells.forEach((c) => {
      map.set(`${c.supplier_id}:${c.accreditation_id}`, c);
    });
    return map;
  }, [matrix]);

  const filteredSuppliers = useMemo(() => {
    if (!matrix) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return matrix.suppliers;
    return matrix.suppliers.filter((s) => s.name.toLowerCase().includes(q));
  }, [matrix, searchTerm]);

  const handleCellClick = (supplierId: number, accreditationId: number) => {
    if (!matrix) return;
    const cell = cellIndex.get(`${supplierId}:${accreditationId}`);
    if (cell) {
      const supplier = matrix.suppliers.find((s) => s.id === supplierId);
      const accreditation = matrix.accreditations.find((a) => a.id === accreditationId);
      setDetailMeta({
        cell,
        supplierName: supplier?.name ?? '',
        accreditationName: accreditation?.name ?? '',
      });
    } else if (isCustomer) {
      setPrefillSupplierId(supplierId);
      setPrefillAccreditationId(accreditationId);
      setEditBindingId(null);
      setEditOpen(true);
    }
  };

  const handleEdit = (bindingId: number) => {
    setDetailMeta(null);
    setEditBindingId(bindingId);
    setPrefillSupplierId(null);
    setPrefillAccreditationId(null);
    setEditOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await qualityAPI.deleteSupplierAccreditation(deleteId);
      toast.success('Accreditation removed');
      setDeleteId(null);
      setDetailMeta(null);
      fetchMatrix();
    } catch {
      toast.error('Failed to remove accreditation');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button onClick={fetchMatrix} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!matrix) return null;

  return (
    <div className="space-y-0">
      <Card className="rounded-lg shadow-sm">
        {/* Tab nav matches Quality.tsx exactly so the strip is consistent
            across NCRs / Concessions / Approval Register. */}
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-2">
              {tabVisibility.ncrs && (
                <Link
                  to="/quality"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  NCRs
                </Link>
              )}
              {showConcessionsTab && (
                <Link
                  to="/concessions"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  Production Permit / Concession
                </Link>
              )}
              {tabVisibility.approval_register && (
                <Link
                  to="/quality/approval-register"
                  className="px-4 py-2 text-sm font-medium rounded-md border-2 border-primary bg-primary text-primary-foreground shadow-sm"
                >
                  Approval Register
                </Link>
              )}
            </nav>
            {isCustomer && (
              <Button
                onClick={() => {
                  setEditBindingId(null);
                  setPrefillSupplierId(null);
                  setPrefillAccreditationId(null);
                  setEditOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Accreditation
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {filteredSuppliers.length} suppliers · {matrix.accreditations.length} accreditations
              {' · expiring window '} {matrix.threshold_days} days
            </div>
          </div>

          {/* Legend */}
          <div className="p-3 border rounded-lg bg-muted/20 flex flex-wrap gap-4 items-center">
            <span className="text-sm font-medium text-muted-foreground">Legend:</span>
            <LegendItem {...STATUS_STYLES.in_date} label="In date" />
            <LegendItem
              {...STATUS_STYLES.expiring}
              label={`Expiring within ${matrix.threshold_days} days`}
            />
            <LegendItem {...STATUS_STYLES.expired} label="Expired" />
            {/* CR-7171: the hollow ring needs naming, or the reader has no way
                to learn that a green dot and an undated one are different. */}
            <LegendItem {...STATUS_STYLES.no_expiry} />
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="inline-block w-4 h-4 rounded-full border border-dashed border-gray-400" />
              Not held
            </span>
          </div>

          {/* Empty states */}
          {matrix.accreditations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              No accreditations defined yet. Add accreditation types under Admin → Quality Config.
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No suppliers match your search.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg shadow-sm">
              <table
                className="w-full border-collapse"
                style={{ minWidth: `${280 + matrix.accreditations.length * 140}px` }}
              >
                <thead>
                  <tr className="border-b-2 border-gray-300 bg-muted/50">
                    <th className="p-3 text-left font-semibold text-sm sticky left-0 bg-muted/50 z-10 min-w-[260px] border-r border-gray-300">
                      Supplier
                    </th>
                    {matrix.accreditations.map((a) => (
                      <th
                        key={a.id}
                        className="p-3 text-center font-semibold text-sm min-w-[120px] max-w-[160px] border-l border-gray-200"
                        title={a.approval_group_name ? `${a.approval_group_name} → ${a.name}` : a.name}
                      >
                        {a.logo_url ? (
                          <div className="flex flex-col items-center gap-1">
                            <img
                              src={a.logo_url}
                              alt={a.name}
                              className="h-10 max-w-[120px] object-contain"
                            />
                            <div className="text-[10px] font-normal text-muted-foreground truncate max-w-[140px]">
                              {a.name}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-xs leading-tight break-words whitespace-normal">
                              {a.name}
                            </div>
                            {a.approval_group_name && (
                              <div className="text-[10px] font-normal text-muted-foreground mt-0.5 truncate">
                                {a.approval_group_name}
                              </div>
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier, idx) => (
                    <tr
                      key={supplier.id}
                      className={`border-b hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                    >
                      <td className="p-3 sticky left-0 bg-inherit z-10 border-r border-gray-300 min-w-[260px]">
                        <button
                          onClick={() => navigate(`/suppliers/${supplier.id}`)}
                          className="text-sm font-medium text-left hover:underline"
                        >
                          {supplier.name}
                        </button>
                      </td>
                      {matrix.accreditations.map((a) => {
                        const cell = cellIndex.get(`${supplier.id}:${a.id}`);
                        return (
                          <td key={a.id} className="p-3 text-center border-l border-gray-100">
                            <MatrixCell
                              cell={cell}
                              expiryFormat={formatDate}
                              canAdd={isCustomer}
                              onClick={() => handleCellClick(supplier.id, a.id)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cell detail modal */}
      <Dialog open={!!detailMeta} onOpenChange={(o) => !o && setDetailMeta(null)}>
        <DialogContent className="max-w-md">
          {detailMeta && (
            <>
              <DialogHeader>
                <DialogTitle>{detailMeta.accreditationName}</DialogTitle>
                <DialogDescription>{detailMeta.supplierName}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <DetailRow
                  label="Status"
                  value={STATUS_STYLES[detailMeta.cell.status].label}
                />
                <DetailRow
                  label="Issued Date"
                  value={detailMeta.cell.issued_date ? formatDate(detailMeta.cell.issued_date) : '—'}
                />
                <DetailRow
                  label="Expiry Date"
                  value={detailMeta.cell.expiry_date ? formatDate(detailMeta.cell.expiry_date) : '—'}
                />
                <DetailRow
                  label="Certificate Number"
                  value={detailMeta.cell.certificate_number || '—'}
                />
                {detailMeta.cell.has_certificate && (
                  <DetailFileLink bindingId={detailMeta.cell.id} />
                )}
                {detailMeta.cell.notes && (
                  <div className="pt-1">
                    <div className="text-muted-foreground mb-1">Notes</div>
                    <div className="whitespace-pre-wrap break-words rounded-md border bg-muted/30 p-3 text-foreground">
                      {detailMeta.cell.notes}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                {isCustomer && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setDeleteId(detailMeta.cell.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                    <Button onClick={() => handleEdit(detailMeta.cell.id)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </>
                )}
                <Button variant="ghost" onClick={() => setDetailMeta(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add / Edit dialog */}
      {isCustomer && (
        <ApprovalRegisterEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          suppliers={matrix.suppliers}
          accreditations={matrix.accreditations}
          existingBindingId={editBindingId}
          prefillSupplierId={prefillSupplierId}
          prefillAccreditationId={prefillAccreditationId}
          onSaved={fetchMatrix}
        />
      )}

      {/* Confirm delete */}
      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove this accreditation?</DialogTitle>
            <DialogDescription>
              The supplier will no longer be marked as holding this accreditation.
              The attached certificate file will be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

